import { TDocument } from '../documents/documents.interfaces';
import { TStatus, TPaymentMode, TTransactionCurrency } from '../finance/finance.interfaces';
import { TPaginatedResponse } from '../__shared/shared.interfaces';

// Routes and Schedule Interfaces
export type TVehicle = {
  id: string;
  vehicleNumber: string;
  vehicleType: 'BUS' | 'VAN' | 'CAR';
  vehicleBrand: string;
  vehicleModel: string;
  vehicleYear: number;
  vehicleColor: string;
  vehicleEngine: string;
  vehicleTransmission: string;
  vehicleFuel: string;
  vehicleMileage: number;
  vehicleImage: TDocument[];
  capacity: number;
  driverId: string;
  routeId: string;
  isActive: boolean;
  lastMaintenanceDate: Date;
  nextMaintenanceDate: Date;
  fuelType: 'PETROL' | 'DIESEL' | 'ELECTRIC' | 'HYBRID';
  registrationNumber: string;
  insuranceExpiryDate: Date;
  permitExpiryDate: Date;
  pricePerKilometer: number;
  priceCurrency?: TTransactionCurrency;
};

export type TRoute = {
  id: string;
  routeName: string;
  startLocation: string;
  endLocation: string;
  stops: TRouteStop[];
  estimatedDuration: number; // in minutes
  distance: number; // in kilometers
  isActive: boolean;
  assignedVehicleId?: string;
  assignedDriverId?: string;
  morningPickupTime: string; // HH:MM format
  afternoonDropTime: string; // HH:MM format
};

export type TRouteStop = {
  id: string;
  stopName: string;
  location: string;
  pickupTime: string; // HH:MM format
  dropTime: string; // HH:MM format
  students: string[]; // student IDs
  coordinates?: {
    latitude: number;
    longitude: number;
  };
};

export type TRouteAssignment = {
  id: string;
  routeId: string;
  vehicleId: string;
  driverId: string;
  assignedStudents: string[]; // student IDs
  academicYear: string;
  isActive: boolean;
  startDate: Date;
  endDate?: Date;
};

export type TDriver = {
  id: string;
  name: string;
  licenseNumber: string;
  licenseType: 'LMV' | 'HMV' | 'PSV';
  licenseExpiryDate: Date;
  phoneNumber: string;
  email?: string;
  address: string;
  emergencyContact: string;
  emergencyContactPhone: string;
  joiningDate: Date;
  salary: number;
  salaryStatus: 'PAID' | 'PENDING' | 'OVERDUE';
  lastSalaryPaidDate?: Date;
  nextSalaryDueDate: Date;
  isActive: boolean;
  experience: number; // in years
  medicalCertificateExpiryDate: Date;
  policeVerificationStatus: 'PENDING' | 'VERIFIED' | 'REJECTED';
  assignedVehicleId?: string;
  assignedRouteId?: string;
};

// Inventory Interfaces
export type TInventoryItem = {
  id: string;
  itemName: string;
  category:
  | 'ELECTRONICS'
  | 'FURNITURE'
  | 'BOOKS'
  | 'SPORTS'
  | 'LAB_EQUIPMENT'
  | 'OFFICE_SUPPLIES'
  | 'UNIFORMS'
  | 'OTHER';
  subCategory?: string;
  description: string;
  quantity: number;
  unit: string; // pieces, kg, liters, etc.
  unitPrice: number;
  totalValue: number;
  supplier: string;
  supplierContact: string;
  location: string; // room, building, etc.
  minimumStock: number;
  maximumStock: number;
  reorderPoint: number;
  lastRestocked: Date;
  nextRestockDate?: Date;
  expiryDate?: Date;
  condition: 'NEW' | 'GOOD' | 'FAIR' | 'POOR' | 'DAMAGED';
  isActive: boolean;
  purchaseDate: Date;
  warrantyExpiryDate?: Date;
  assignedTo?: string; // staff or department ID
  notes?: string;
};

export type TInventoryTransaction = {
  id: string;
  itemId: string;
  transactionType:
  | 'PURCHASE'
  | 'SALE'
  | 'TRANSFER'
  | 'DAMAGE'
  | 'LOSS'
  | 'ADJUSTMENT';
  quantity: number;
  unitPrice: number;
  totalAmount: number;
  transactionDate: Date;
  referenceNumber?: string;
  notes?: string;
  performedBy: string; // staff ID
  paymentStatus: TStatus;
  paymentMode?: TPaymentMode;
  supplier?: string;
  recipient?: string;
};

export type TInventoryCategory = {
  id: string;
  name: string;
  description: string;
  parentCategoryId?: string;
  isActive: boolean;
  totalItems: number;
  totalValue: number;
};

// Health and Safety Interfaces
export type THealthIncident = {
  id: string;
  incidentType:
  | 'INJURY'
  | 'ILLNESS'
  | 'ACCIDENT'
  | 'EMERGENCY'
  | 'ALLERGY'
  | 'OTHER';
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  reportedBy: string; // staff ID
  reportedDate: Date;
  incidentDate: Date;
  location: string;
  description: string;
  affectedPersonType: 'STUDENT' | 'STAFF' | 'VISITOR';
  affectedPersonId: string;
  symptoms?: string;
  firstAidProvided: boolean;
  medicalAttentionRequired: boolean;
  hospitalVisited?: string;
  followUpRequired: boolean;
  followUpDate?: Date;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
  resolution?: string;
  preventiveMeasures?: string;
};

export type TSafetyProtocol = {
  id: string;
  protocolName: string;
  category:
  | 'FIRE_SAFETY'
  | 'FIRST_AID'
  | 'EMERGENCY_EVACUATION'
  | 'FOOD_SAFETY'
  | 'TRANSPORT_SAFETY'
  | 'LAB_SAFETY'
  | 'SPORTS_SAFETY';
  description: string;
  procedures: string[];
  responsiblePerson: string; // staff ID
  lastUpdated: Date;
  nextReviewDate: Date;
  isActive: boolean;
  attachments?: string[]; // file paths
  trainingRequired: boolean;
  lastTrainingDate?: Date;
  nextTrainingDate?: Date;
};

export type TEmergencyContact = {
  id: string;
  name: string;
  relationship:
  | 'PARENT'
  | 'GUARDIAN'
  | 'EMERGENCY_CONTACT'
  | 'DOCTOR'
  | 'HOSPITAL';
  phoneNumber: string;
  alternatePhoneNumber?: string;
  email?: string;
  address?: string;
  isActive: boolean;
  priority: number; // 1 = primary, 2 = secondary, etc.
  personId: string; // student or staff ID
  personType: 'STUDENT' | 'STAFF';
};

export type THealthCheckup = {
  id: string;
  personId: string;
  personType: 'STUDENT' | 'STAFF';
  checkupDate: Date;
  checkupType:
  | 'ANNUAL'
  | 'MEDICAL'
  | 'DENTAL'
  | 'VISION'
  | 'HEARING'
  | 'VACCINATION';
  doctorName: string;
  hospitalClinic: string;
  findings: string;
  recommendations: string;
  nextCheckupDate?: Date;
  isCleared: boolean;
  notes?: string;
  attachments?: TDocument[]; // medical reports, etc.
};

// Administration View Props
export type AdministrationViewProps = {
  // view props goes here
};

// Mock Data Interfaces
export type TAdministrationMockData = {
  routes: TRoute[];
  vehicles: TVehicle[];
  routeAssignments: TRouteAssignment[];
  drivers: TDriver[];
  inventoryItems: TInventoryItem[];
  inventoryTransactions: TInventoryTransaction[];
  inventoryCategories: TInventoryCategory[];
  healthIncidents: THealthIncident[];
  safetyProtocols: TSafetyProtocol[];
  emergencyContacts: TEmergencyContact[];
  healthCheckups: THealthCheckup[];
};

export enum TViewType {
  ROUTES = 'routes',
  VEHICLES = 'vehicles',
  ASSIGNMENTS = 'assignments',
  SALARIES = 'salaries',
}

export type TRowClickHandlerType =
  | TDriver
  | TVehicle
  | TRoute
  | TRouteAssignment;

export type IRoutesResponse = TPaginatedResponse<TRoute>;
export type IVehiclesResponse = TPaginatedResponse<TVehicle>;
export type IRouteAssignmentsResponse = TPaginatedResponse<TRouteAssignment>;
export type IDriversResponse = TPaginatedResponse<TDriver>;
